import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { type WorkspaceParent } from 'api/workspace/endpoints/getWorkspaceContents';
import { type WorkspaceModel } from './WorkspaceModel';

// The selected container lives in the `container` query param (e.g. `zone:3`).
const CONTAINER_PARAM = 'container';
const LOCATOR_RE = /^(zone|folder):\d+$/;

const isValidLocator = (value: string | null): value is WorkspaceParent => value !== null && LOCATOR_RE.test(value);

/**
 * Bridges the router-free `WorkspaceModel` selection to the URL: hydrates the
 * model from `?container=` on mount (so a refresh restores the selection) and
 * mirrors later selections back into the URL. Must be called from an `observer`
 * component so the model→URL effect re-runs when the selection changes.
 *
 * The two effects don't loop: after either side updates, `selected` and the URL
 * agree, so the opposite effect's guard is false.
 */
export const useWorkspaceUrlSync = (model: WorkspaceModel): void => {
  const [params, setParams] = useSearchParams();
  const urlContainer = params.get(CONTAINER_PARAM);

  // URL → model (mount + external navigation, e.g. back/forward). `hydrate`
  // expands the tree to a deep folder before selecting it.
  useEffect(() => {
    if (isValidLocator(urlContainer) && model.selected !== urlContainer) {
      model.hydrate(urlContainer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlContainer]);

  // model → URL.
  const selected = model.selected;
  useEffect(() => {
    if (selected !== null && selected !== urlContainer) {
      const next = new URLSearchParams(params);
      next.set(CONTAINER_PARAM, selected);
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);
};
